import { useState } from 'react';
import { Rocket, CheckCircle, ArrowRight, Zap, TrendingUp, Shield } from 'lucide-react';
import { WebsiteAnalysis } from '../lib/api';

interface ConsultationAdProps {
  analysis?: WebsiteAnalysis;
}

export function ConsultationAd({ analysis }: ConsultationAdProps) {
  const [pointOfContact, setPointOfContact] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const criticalIssues = analysis
    ? [
        (analysis.structuredDataScore || 0) < 50 ? 'Missing critical structured data' : null,
        (analysis.accessibilityScore || 0) < 50 ? 'Accessibility barriers blocking AI crawlers' : null,
        (analysis.technicalSeoScore || 0) < 50 ? 'Technical SEO gaps limiting discoverability' : null,
        (analysis.contentQualityScore || 0) < 50 ? 'Content structure needs optimization' : null,
      ].filter(Boolean)
    : [];

  const hasSignificantIssues = analysis ? (analysis.overallScore || 0) < 70 : false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!pointOfContact.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!company.trim()) {
      setError('Please enter your company name');
      return;
    }

    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    if (!analysis && !website.trim()) {
      setError('Please enter your website URL');
      return;
    }

    setIsSubmitting(true);

    try {
      const submittedWebsite = analysis ? analysis.url : website.trim();
      const overallScore = analysis ? (analysis.overallScore || 0) : null;
      const message = analysis
        ? `AI Readiness consultation request. Current score: ${overallScore}/100`
        : 'AI Readiness consultation request submitted from Contact Us page.';

      const response = await fetch('/api/consultation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: pointOfContact.trim(),
          email: email.trim(),
          company: company.trim(),
          phone: phone.trim(),
          website: submittedWebsite,
          message,
          analysisId: analysis ? analysis.id : null,
          overallScore: overallScore ?? 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to submit consultation request');
      }

      setIsSubmitted(true);
    } catch (err) {
      console.error('Form submission error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setError(`Error: ${errorMessage}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-full mb-4">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-medium text-gray-900 mb-2">You're All Set!</h3>
          <p className="text-gray-600 mb-4">
            We'll reach out within 24 hours to schedule your free consultation.
          </p>
          <p className="text-sm text-gray-500">
            In the meantime, check your inbox for resources to get started on quick wins.
          </p>
        </div>
      </div>
    );
  }

  const headline = analysis
    ? hasSignificantIssues
      ? "Don't Let AI Search Engines Pass You By"
      : 'Take Your AI Readiness to the Next Level'
    : 'Talk to an AI Search Specialist';

  const subhead = analysis
    ? hasSignificantIssues
      ? `Your site scored ${analysis.overallScore || 0}/100. Every point you're missing means lost visibility in AI search results.`
      : `You're close to perfect AI readiness. Let us help you achieve that final 20% for maximum impact.`
    : 'Get a free, no-commitment consultation with our AI SEO experts at Round 1 Solutions.';

  return (
    <div className="bg-black rounded-xl overflow-hidden">
      <div className="p-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
            <Rocket className="w-7 h-7 text-black" />
          </div>
          <div className="flex-1">
            <div className="inline-block bg-white text-black text-xs font-medium px-3 py-1 rounded-full mb-2">
              {analysis ? 'LIMITED TIME OFFER' : 'FREE CONSULTATION'}
            </div>
            <h3 className="text-2xl font-medium text-white mb-2">{headline}</h3>
            <p className="text-gray-300 text-lg">{subhead}</p>
          </div>
        </div>

        {hasSignificantIssues && criticalIssues.length > 0 && (
          <div className="bg-gray-900 rounded-lg p-4 mb-6 border border-gray-700">
            <p className="text-white font-medium mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Critical Issues Detected:
            </p>
            <ul className="space-y-2">
              {criticalIssues.slice(0, 3).map((issue, index) => (
                <li key={index} className="text-gray-300 text-sm flex items-start gap-2">
                  <span className="text-white mt-0.5">⚠</span>
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
            <Zap className="w-6 h-6 text-white mb-2" />
            <h4 className="text-white font-medium mb-1">Fast Results</h4>
            <p className="text-gray-300 text-sm">See improvements within 2-4 weeks</p>
          </div>
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
            <TrendingUp className="w-6 h-6 text-white mb-2" />
            <h4 className="text-white font-medium mb-1">Proven Track Record</h4>
            <p className="text-gray-300 text-sm">Average 45% score improvement</p>
          </div>
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
            <Shield className="w-6 h-6 text-white mb-2" />
            <h4 className="text-white font-medium mb-1">Future-Proof</h4>
            <p className="text-gray-300 text-sm">Stay ahead of AI search trends</p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6">
          <h4 className="text-xl font-medium text-gray-900 mb-2">
            Get Your Free AI Readiness Consultation
          </h4>
          <p className="text-gray-600 mb-4">
            Our experts at <span className="font-medium text-black">Round 1 Solutions</span> will create a custom roadmap to fix these issues and skyrocket your AI search visibility.
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <p className="text-sm font-medium text-gray-900 mb-2">Your Free Consultation Includes:</p>
            <ul className="space-y-1.5">
              <li className="text-sm text-gray-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-black flex-shrink-0" />
                <span>30-minute strategy session with an AI SEO specialist</span>
              </li>
              <li className="text-sm text-gray-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-black flex-shrink-0" />
                <span>Custom implementation roadmap prioritized by impact</span>
              </li>
              <li className="text-sm text-gray-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-black flex-shrink-0" />
                <span>Competitive analysis vs. top-ranking sites in your niche</span>
              </li>
              <li className="text-sm text-gray-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-black flex-shrink-0" />
                <span>Quick-win recommendations you can implement today</span>
              </li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <input
                type="text"
                value={pointOfContact}
                onChange={(e) => setPointOfContact(e.target.value)}
                placeholder="Point of Contact (Your Name)"
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Company Name"
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {!analysis && (
              <div>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="Website URL (e.g. https://example.com)"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            )}

            <div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone Number"
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-black text-white font-medium py-4 px-6 rounded-lg hover:bg-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                'Submitting...'
              ) : (
                <>
                  Claim Your Free Consultation
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-3">
            No commitment required. We respect your privacy and won't spam you.
          </p>
        </div>

        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm">
            Get ahead of your competition and improve your AI search rankings with Round 1 Solutions
          </p>
        </div>
      </div>
    </div>
  );
}
